import { Container, Row, Col, Spinner } from 'react-bootstrap';
import { COLORS } from './Mobile';
import Textarea from 'components/bootstrap/forms/Textarea';
import { useContext, useRef, useState, useEffect } from 'react';
import Button from 'components/bootstrap/Button';
import showNotification from 'components/extras/showNotification';
import { usePlanningData } from './hooks/usePlanningData';
import SvgGallery from 'assets/customizeIcons/svg-icons/GalleryIcon';
import { deleteAPI, postAPI } from 'apis/backend/apiNode';
import AuthContext from 'contexts/authContext';

const RapportTabMobile = ({ interventionData, setInterventionData }) => {
	const [saveStatus, setSaveStatus] = useState('saved'); // 'saved' | 'typing' | 'saving'
	const { updateIntervention } = usePlanningData();
	const { userData } = useContext(AuthContext);
	const [uploading, setUploading] = useState(false);
	const [deletingAttachment, setDeletingAttachment] = useState(null);

	const fileInputRef = useRef(null);
	const debounceRef = useRef(null);

	// Cleanup debounce on unmount
	useEffect(() => {
		return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
	}, []);

	const performAutoSave = async (newValue) => {
		setSaveStatus('saving');
		try {
			await updateIntervention(interventionData?._id, {
				rapport_intervention: {
					...interventionData?.rapport_intervention,
					description_travaux: newValue,
					description_travaux_draft: '',
				},
			});
			setSaveStatus('saved');
		} catch (err) {
			setSaveStatus('typing');
			showNotification('Erreur', 'Sauvegarde échouée', 'danger');
		}
	};

	const handleTextChange = (e) => {
		const value = e.target.value;
		setSaveStatus('typing');

		setInterventionData((prev) => ({
			...prev,
			rapport_intervention: {
				...(prev?.rapport_intervention || {}),
				description_travaux: value,
				description_travaux_draft: value,
			},
		}));

		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => performAutoSave(value), 1000);
	};
	// Fonction pour uploader les pièces jointes
	const handleSaveChoice = async (manualData = null) => {
		setSaveStatus('saving');
		try {
			// Use manualData if provided (fresh from click), otherwise fallback to current state
			const rapportToSave = manualData
				? { ...interventionData?.rapport_intervention, ...manualData }
				: (interventionData?.rapport_intervention || {});

			await updateIntervention(interventionData?._id, {
				rapport_intervention: rapportToSave
			});

			setSaveStatus('saved');
		} catch (err) {
			console.error('❌ Error saving rapport:', err);
			setSaveStatus('typing'); // Show it's not synced
			showNotification('Erreur', err.message, 'danger');
		}
	};
	const uploadAttachments = async (files) => {

		if (!files?.length) return;



		setUploading(true);



		try {

			const formData = new FormData();



			files.forEach((file) => {

				formData.append('attachments', file);

			});



			const uploadResponse = await postAPI(

				'attachment',

				formData,

				`/rapport/${interventionData?._id}`,

			);



			if (uploadResponse?.files?.length) {

				const formattedFiles = uploadResponse?.files?.map((file) => ({

					originalName: file.originalName || file.name,

					fileName:

						file.fileName || `att_${Date.now()}_${Math.random().toString(36).slice(2)}`,

					mimeType: file.mimeType || file.type || 'application/octet-stream',

					size: file.size || 0,

					path:

						file.path ||

						`uploads/rapport/${interventionData?._id}/${file.fileName || file.name}`,

					uploadedAt: new Date().toISOString(),

					uploadedBy: userData?._id || userData?.id,

				}));



				const updatedAttachments = [

					...(interventionData?.rapport_intervention?.attachments || []),

					...formattedFiles,

				];



				// 🔥 AUTO SAVE TO API

				await updateIntervention(interventionData?._id, {

					rapport_intervention: {

						...interventionData?.rapport_intervention,

						attachments: updatedAttachments,

					},

				});



				// 🔥 UPDATE LOCAL STATE

				setInterventionData((prev) => ({

					...prev,

					rapport_intervention: {

						...(prev?.rapport_intervention || {}),

						attachments: updatedAttachments,

					},

				}));



				showNotification('Succès', 'Pièce jointe ajoutée avec succès', 'success');

			}

		} catch (err) {

			console.error("Erreur lors de l'upload:", err);

			showNotification('Erreur', "Erreur lors de l'upload des pièces jointes", 'danger');

		} finally {

			setUploading(false);

		}

	};



	// Fonction pour supprimer une pièce jointe

	const deleteAttachment = async (attachmentPath) => {

		setDeletingAttachment(attachmentPath);

		try {

			await deleteAPI('attachment', `/${attachmentPath}`);

			const newAttachment = interventionData?.rapport_intervention?.attachments?.filter(

				(att) => att.path !== attachmentPath,

			);



			// 🔥 AUTO SAVE TO API

			await updateIntervention(interventionData?._id, {

				rapport_intervention: {

					...interventionData?.rapport_intervention,

					attachments: newAttachment,

				},

			});



			// 🔥 UPDATE LOCAL STATE

			setInterventionData((prev) => ({

				...prev,

				rapport_intervention: {

					...(prev?.rapport_intervention || {}),

					attachments: newAttachment,

				},

			}));



			showNotification('Succès', 'Pièce jointe supprimée avec succès', 'success');

		} catch (err) {

			console.error('Erreur lors de la suppression de la pièce jointe:', err);

			showNotification(

				'Erreur',

				'Erreur lors de la suppression de la pièce jointe',

				'danger',

			);

		} finally {

			setDeletingAttachment(null);

		}

	};



	// Gestion des fichiers sélectionnés

	const handleFileChange = (e) => {

		const selectedFiles = Array.from(e.target.files);

		uploadAttachments(selectedFiles);

	};



	const getFileType = (file) => {

		const mime = file?.mimeType || '';

		const name = file?.originalName || file?.fileName || '';



		if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp)$/i.test(name)) {

			return 'image';

		}

		if (mime.startsWith('video/') || /\.(mp4|webm|ogg|mov)$/i.test(name)) {

			return 'video';

		}

		if (mime === 'application/pdf' || /\.pdf$/i.test(name)) {

			return 'pdf';

		}

		return 'file';

	};
	return (
		<Container fluid className="p-3">
			{/* INJECTED CSS FOR CUSTOM COMPONENTS */}
			<style>{`
                .status-badge { font-size: 0.75rem; padding: 4px 8px; border-radius: 12px; font-weight: 600; }
                .status-saved { background: #e2f9f0; color: #1fb47a; }
                .status-typing { background: #fff4e5; color: #ff9800; }
                .status-saving { background: #e8f2ff; color: #007bff; }
                
                .upload-card {
                    border: 2px dashed ${COLORS.PRIMARY};
                    background-color: ${COLORS.BG_L10_PRIMARY};
                    border-radius: 12px;
                    transition: all 0.2s;
                }
                .attachment-preview {
                    height: 150px;
                    object-fit: cover;
                    border-radius: 8px;
                    width: 100%;
                }
                .delete-overlay {
                    position: absolute; top: 5px; right: 5px;
                    background: rgba(255, 0, 0, 0.7);
                    color: white; border: none; border-radius: 50%;
                    width: 24px; height: 24px; display: flex; align-items: center; justify-content: center;
                }
            `}</style>


			{/* HEADER & STATUS */}
			<div className="d-flex justify-content-between align-items-center mb-2">
				<h6 className="fw-bold m-0" style={{ color: '#172B4D' }}>Compte Rendu</h6>
				<div className={`status-badge status-${saveStatus}`}>
					{saveStatus === 'saved' && '✓ Sauvegardé'}
					{saveStatus === 'typing' && '... En cours de saisie'}
					{saveStatus === 'saving' && <><Spinner animation="border" size="sm" className="me-1" /> Sauvegarde...</>}
				</div>
			</div>

			{/* TEXTAREA */}
			<div className="mb-4">
				<Textarea
					rows={8}
					className="shadow-sm"
					value={interventionData?.rapport_intervention?.description_travaux || ''}
					onChange={handleTextChange}
					placeholder="Décrivez les travaux effectués..."
				/>
			</div>

			{/* POSE STATUS */}
			{interventionData?.type === 'pose' && (
				<div className="mb-4">
					<h6 className="fw-bold mb-2 text-dark">Statut de la pose</h6>
					<Row className="g-2">
						<Col xs={6}>
							<label className={`w-100 p-3 border rounded d-flex align-items-center cursor-pointer ${interventionData?.rapport_intervention?.pose_faite ? 'bg-primary text-white border-primary' : 'bg-light'}`}>
								<input
									type="radio"
									className="me-2"
									checked={!!interventionData?.rapport_intervention?.pose_faite}
									onChange={() => {
										const change = { pose_faite: true };
										setInterventionData(prev => ({
											...prev,
											rapport_intervention: { ...prev.rapport_intervention, ...change }
										}));
										handleSaveChoice(change);
									}}
								/>
								Pose faite
							</label>
						</Col>
						<Col xs={6}>
							<label className={`w-100 p-3 border rounded d-flex align-items-center cursor-pointer ${!interventionData?.rapport_intervention?.pose_faite ? 'bg-primary text-white border-primary' : 'bg-light'}`}>
								<input
									type="radio"
									className="me-2"
									checked={!interventionData?.rapport_intervention?.pose_faite}
									onChange={() => {
										const change = { pose_faite: false };
										setInterventionData(prev => ({
											...prev,
											rapport_intervention: { ...prev.rapport_intervention, ...change }
										}));
										handleSaveChoice(change);
									}}
								/>
								En cours
							</label>
						</Col>
					</Row>
				</div>
			)}

			{/* DEVIS STATUS */}
			{interventionData?.type === 'devis' && (
				<div className='mb-4'>
					<label className="w-100 p-3 border rounded d-flex align-items-center bg-light cursor-pointer shadow-sm">
						<input
							type='checkbox'
							className="me-3 form-check-input"
							style={{ width: '20px', height: '20px' }}
							checked={!!interventionData?.rapport_intervention?.pose_sur_place}
							onChange={(e) => {
								const isChecked = e.target.checked;
								const change = { pose_sur_place: isChecked };

								// 1. Update UI locally
								setInterventionData((prev) => ({
									...prev,
									rapport_intervention: {
										...(prev?.rapport_intervention || {}),
										...change
									},
								}));

								// 2. Push to API immediately
								handleSaveChoice(change);
							}}
						/>
						<span className="fw-medium">Devis accepté et Pose sur place</span>
					</label>
				</div>
			)}
			{/* ===== ATTACHMENTS ===== */}

			<div>

				<h6

					style={{

						fontWeight: 600,

						color: '#172B4D',

						marginBottom: 10,

					}}>

					Pièces jointes{' '}

					<small className='text-muted fw-normal ms-2'>

						(image, videos , pdf ...)

					</small>

				</h6>



				{/* ACTIONS */}

				<Button

					className='my-4 w-100'

					disabled={uploading}

					onClick={() => fileInputRef.current?.click()}

					style={{

						backgroundColor: COLORS.BG_L10_PRIMARY,

						height: 70,

						borderRadius: 10,

						display: 'flex',

						flexDirection: 'column',

						alignItems: 'center',

						justifyContent: 'center',

						gap: 6,

						border: 'none',

						cursor: uploading ? 'not-allowed' : 'pointer',

					}}>

					{uploading ? (

						<Spinner

							animation='border'

							size='sm'

							style={{ color: COLORS.PRIMARY }}

						/>

					) : (

						<SvgGallery size={30} color={COLORS.PRIMARY} />

					)}



					<span

						style={{

							fontSize: 12,

							fontWeight: 600,

							color: COLORS.PRIMARY,

						}}>

						{uploading ? 'Upload...' : 'Ajouter les images'}

					</span>



					<input

						ref={fileInputRef}

						type='file'

						multiple

						onChange={handleFileChange}

						style={{ display: 'none' }}

					/>

				</Button>



				{/* ATTACHMENT GRID */}

				{interventionData?.rapport_intervention?.attachments?.length > 0 && (

					<Row className='g-3'>

						{interventionData.rapport_intervention.attachments.map(

							(file, idx) => {

								const type = getFileType(file);

								const fileUrl = '/coreService/' + file?.path;

								const isDeleting = deletingAttachment === file.path;



								return (

									<Col key={idx} xs={12} sm={6} md={4} lg={3} xl={2}>

										<div

											style={{

												position: 'relative',

												border: '1px solid #e7e7e7',

												borderRadius: 10,

												overflow: 'hidden',

												background: '#fafafa',

												opacity: isDeleting ? 0.6 : 1,

											}}>

											{/* ===== DELETE BUTTON ===== */}

											<button

												disabled={isDeleting}

												onClick={() => deleteAttachment(file.path)}

												style={{

													position: 'absolute',

													top: 8,

													right: 8,

													zIndex: 10,

													border: 'none',

													background: 'rgba(0,0,0,0.6)',

													color: '#fff',

													borderRadius: '50%',

													width: 28,

													height: 28,

													display: 'flex',

													alignItems: 'center',

													justifyContent: 'center',

													cursor: 'pointer',

												}}>

												{isDeleting ? (

													<span

														className='spinner-border spinner-border-sm'

														role='status'

													/>

												) : (

													'×'

												)}

											</button>

											<a

												href={fileUrl}

												download

												style={{

													textDecoration: 'none',

													color: 'inherit',

												}}>

												{/* ===== PREVIEW ===== */}

												<div

													style={{

														height: 190,

														display: 'flex',

														alignItems: 'center',

														justifyContent: 'center',

														backgroundColor: '#f5f5f5',

													}}>

													{/* IMAGE */}

													{type === 'image' && (

														<img

															src={fileUrl}

															alt={file.originalName}

															style={{

																width: '100%',

																height: '100%',

																objectFit: 'contain',

															}}

														/>

													)}



													{/* VIDEO */}

													{type === 'video' && (

														<video

															src={fileUrl}

															controls

															style={{

																width: '100%',

																height: '100%',

																objectFit: 'cover',

															}}

														/>

													)}



													{/* PDF */}

													{type === 'pdf' && (

														<iframe

															src={fileUrl}

															type='application/pdf'

															style={{

																objectFit: 'fill',

																width: '100%',

																height: '100%',

															}}

														/>

													)}



													{/* OTHER FILE */}

													{type === 'file' && (

														<div

															style={{ textAlign: 'center' }}>

															<img

																src='/icons/file-icon.svg'

																alt='File'

																style={{

																	width: 40,

																	marginBottom: 8,

																}}

															/>

															<div style={{ fontSize: 12 }}>

																Fichier

															</div>

														</div>

													)}

												</div>

											</a>

											{/* ===== FOOTER ===== */}

										</div>

									</Col>

								);

							},

						)}

					</Row>

				)}

			</div>

		</Container>
	);
};

export default RapportTabMobile;